<template>
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.dashboard.admin' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard")
                }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.ads' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.ads")
                }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2"><span
                class="tw-text-sm">{{
                    $t("dashboard.sidebar.analytics") }}</span></button>
    </div>

    <div class="tw-min-h-[700px] tw-p-2">
        <!-- Commenting out filter-wrapper instead of using v-if="false" to avoid linter error -->
        <filter-wrapper :filters="filters" @filter="onFilter" />

        <!-- Stats cards section -->
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-2 tw-my-2">
            <!-- Total Sales Card -->
            <div class="tw-bg-white  tw-rounded-md tw-p-4 tw-border tw-border-gray-200">
                <h3 class="tw-text-gray-500 tw-font-medium tw-mb-2 tw-text-sm">Total Sales</h3>
                <div v-if="!loading.kpis" class="tw-flex tw-flex-col">
                    <span class="tw-text-3xl tw-font-bold tw-text-gray-800">{{ data.total_orders_count }}</span>
                    <div class="tw-flex tw-items-center tw-mt-2" v-if="false">
                        <span class="tw-text-green-600 tw-font-semibold tw-text-sm">+0%</span>
                        <span class="tw-text-gray-500 tw-ml-2 tw-text-xs">+0k this week</span>
                    </div>
                </div>
                <div v-else class="tw-animate-pulse">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                </div>
            </div>

            <!-- Total Spend Card -->
            <div class="tw-bg-white  tw-rounded-md tw-p-4 tw-border tw-border-gray-200">
                <h3 class="tw-text-gray-500 tw-font-medium tw-mb-2 tw-text-sm">Total Spend</h3>
                <div v-if="!loading.kpis" class="tw-flex tw-flex-col">
                    <span class="tw-text-3xl tw-font-bold tw-text-gray-800">${{ data.total_spent.toFixed(2) }}</span>
                    <div class="tw-flex tw-items-center tw-mt-2" v-if="false">
                        <span class="tw-text-green-600 tw-font-semibold tw-text-sm">+0%</span>
                        <span class="tw-text-gray-500 tw-ml-2 tw-text-xs">+0k this week</span>
                    </div>
                </div>
                <div v-else class="tw-animate-pulse">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                </div>
            </div>

            <!-- Cost Per Lead Card -->
            <div class="tw-bg-white  tw-rounded-md tw-p-4 tw-border tw-border-gray-200">
                <h3 class="tw-text-gray-500 tw-font-medium tw-mb-2 tw-text-sm">Cost Per Lead</h3>
                <div v-if="!loading.kpis" class="tw-flex tw-flex-col">
                    <span class="tw-text-3xl tw-font-bold tw-text-gray-800">$ {{ data.cost_per_lead.toFixed(2) }}</span>
                    <div class="tw-flex tw-items-center tw-mt-2" v-if="false">
                        <span class="tw-text-green-600 tw-font-semibold tw-text-sm">+0%</span>
                        <span class="tw-text-gray-500 tw-ml-2 tw-text-xs">+0 this week</span>
                    </div>
                </div>
                <div v-else class="tw-animate-pulse">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                </div>
            </div>

            <!-- Profile Per Order Card -->
            <div class="tw-bg-white  tw-rounded-md tw-p-4 tw-border tw-border-gray-200">
                <h3 class="tw-text-gray-500 tw-font-medium tw-mb-2 tw-text-sm">Profit Per Order</h3>
                <div v-if="!loading.kpis" class="tw-flex tw-flex-col">
                    <span class="tw-text-3xl tw-font-bold tw-text-gray-800">$ {{ profitPerOrder.toFixed(2) }}</span>
                    <div class="tw-flex tw-items-center tw-mt-2" v-if="false">
                        <span class="tw-text-red-600 tw-font-semibold tw-text-sm">0%</span>
                        <span class="tw-text-gray-500 tw-ml-2 tw-text-xs">+0$ this week</span>
                    </div>
                </div>
                <div v-else class="tw-animate-pulse">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                </div>
            </div>

            <!-- Chiffre d'affaire Card -->
            <div class="tw-bg-white  tw-rounded-md tw-p-4 tw-border tw-border-gray-200">
                <h3 class="tw-text-gray-500 tw-font-medium tw-mb-2 tw-text-sm">Turnover</h3>
                <div v-if="!loading.kpis" class="tw-flex tw-flex-col">
                    <span class="tw-text-3xl tw-font-bold tw-text-gray-800">${{ getValueInUSD(data.total_delivered_turnover).toFixed(2) }}</span>
                    <div class="tw-flex tw-items-center tw-mt-2" v-if="false">
                        <span class="tw-text-red-600 tw-font-semibold tw-text-sm">0%</span>
                        <span class="tw-text-gray-500 tw-ml-2 tw-text-xs">+0$ this week</span>
                    </div>
                </div>
                <div v-else class="tw-animate-pulse">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                </div>
            </div>
        </div>

        <!-- Charts section -->
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-12 tw-gap-4">
            <!-- Total Leads Chart -->
            <div
                class="tw-col-span-12 md:tw-col-span-6 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-p-4">
                <h3 class="tw-text-lg tw-font-semibold tw-mb-4">Toatal Leads</h3>
                <div v-if="!loading.leadsByRange">
                    <apexchart :key="loading.leadsByRange" type="line" height="350" :options="lineChartOptions" :series="lineChartSeries">
                    </apexchart>
                </div>
                <div v-else class="tw-animate-pulse tw-h-[350px] tw-bg-gray-200 tw-rounded"></div>
            </div>

            <!-- Confirmation Rate Chart -->
            <div
                class="tw-col-span-12 md:tw-col-span-3 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-p-4">
                <h3 class="tw-text-lg tw-font-semibold tw-mb-4">Confirmation Rate</h3>
                <div v-if="!loading.confirmationRate">
                    <apexchart type="donut" height="350" :options="donutChartOptions" :series="confirmationRateSeries">
                    </apexchart>
                </div>
                <div v-else class="tw-animate-pulse tw-h-[350px] tw-bg-gray-200 tw-rounded"></div>
            </div>

            <!-- Delivery Rate Chart -->
            <div
                class="tw-col-span-12 md:tw-col-span-3 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-p-4">
                <h3 class="tw-text-lg tw-font-semibold tw-mb-4">Delivery Rate</h3>
                <div v-if="!loading.deliveryRate">
                    <apexchart type="donut" height="350" :options="donutChartOptions" :series="deliveryRateSeries">
                    </apexchart>
                </div>
                <div v-else class="tw-animate-pulse tw-h-[350px] tw-bg-gray-200 tw-rounded"></div>
            </div>
        </div>
    </div>
</template>


<script setup>
import { ads, leadsByRange } from '@/api/analytics';
import FilterWrapper from '@/views/admin/ads/partials/analytics/FilterWrapper.vue';
import { ref, provide, computed } from 'vue';
import moment from 'moment';

// Loading state
const loading = ref({
  leadsByRange: false,
  kpis: false,
  confirmationRate: false,
  deliveryRate: false
});

// Filters
const filters = ref({
  created_at: { from: null, to: null },
  treated_at: { from: null, to: null },
  dropped_at: { from: null, to: null },
  agent_status: [],
  agent_id: [],
  product_id: [],
  delivery_status: [],
  marketer_id: [],
  source_id: []
});

const callbacks = ref([]);
const data = ref({});
const exchangeRate = ref(4.88);

const onFilter = () => {
  fetchData();
}

const totalProfit = computed(() => {
  return getValueInUSD(data.value.total_delivered_turnover) - data.value.total_spent - data.value.total_product_cost - getValueInUSD(data.value.total_shipping_cost) - data.value.total_delivered_orders;
})

const profitPerOrder = computed(() => {
  if (!data.value.total_delivered_orders) return 0;
  return totalProfit.value / data.value.total_delivered_orders;
})

const getValueInUSD = (value) => {
  if (!value) return 0;
  return value / exchangeRate.value;
}

provide('filters', filters);
provide('update:filters', v => {
  filters.value = v
  console.log('fi: ', filters.value)
})
provide('register', callback => callbacks.value.push(callback));




// Line chart for Total Leads
const lineChartSeries = ref([
  {
    name: "Total Leads",
    data: [28, 45, 55, 75, 85, 98, 112, 135, 148, 158, 210, 245]
  }
]);

const lineChartOptions = ref({
  chart: {
    type: 'line',
    toolbar: {
      show: false
    }
  },
  stroke: {
    curve: 'smooth',
    width: 3,
    colors: ['#10B981']
  },
  xaxis: {
    categories: ['Jan 1', 'Jan 5', 'Jan 10', 'Jan 15', 'Jan 20', 'Jan 25', 'Jan 30', 'Feb 5', 'Feb 10', 'Feb 15', 'Feb 20', 'Feb 25'],
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '12px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 400
      },
      rotateAlways: false,
      hideOverlappingLabels: true
    },
    axisBorder: {
      show: false
    },
    axisTicks: {
      show: false
    }
  },
  grid: {
    borderColor: '#f1f1f1',
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'light',
      type: 'vertical',
      shadeIntensity: 0.5,
      gradientToColors: ['rgba(16, 185, 129, 0.1)'],
      inverseColors: false,
      opacityFrom: 0.8,
      opacityTo: 0.1,
    },
  },
  markers: {
    size: 4,
    colors: ['#10B981'],
    strokeColors: '#fff',
    strokeWidth: 2,
    hover: {
      size: 6,
    }
  },
  yaxis: {
    min: 0,
    max: 250,
    tickAmount: 5,
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '12px'
      }
    }
  },
  tooltip: {
    x: {
      show: true,
      format: 'dd MMM',
      formatter: undefined,
    }
  }
});

// Donut chart shared options
const donutChartOptions = ref({
  chart: {
    type: 'donut',
  },
  legend: {
    position: 'bottom'
  },
  colors: ['#F87171', '#A78BFA', '#FCD34D', '#34D399'],
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    pie: {
      donut: {
        size: '75%',
        labels: {
          show: true,
          name: {
            show: true,
          },
          value: {
            show: true,
            formatter: function(val) {
              return val + '%';
            }
          },
          total: {
            show: true,
            label: 'Total',
            formatter: function() {
              return '100%';
            }
          }
        }
      }
    }
  },
  labels: ['cancel', 'reported', 'pas de reponse', 'confirmed'],
});

// Series for confirmation rate chart
const confirmationRateSeries = [29.1, 22.2, 15.6, 33];

// Series for delivery rate chart
const deliveryRateSeries = [29.1, 22.2, 15.6, 33];


const fetchLeadsByRange = async () => {
  loading.value.leadsByRange = true;

  await leadsByRange(filters.value)
  .then(response => {
    
    lineChartSeries.value = [
      {
        name: "Total Leads",
        data: response.data.data.map(item => item.total_leads)
      }
    ]
    
    lineChartOptions.value.xaxis.categories = response.data.data.map(item => moment(item.date).format('DD MMM'));
  })
  .catch(error => {
    console.error(error);
  })

  loading.value.leadsByRange = false;
}

const fetchKpis = async () => {
  loading.value.kpis = true;

  await ads(filters.value)
  .then(response => {
    console.log(response);
    data.value = response.data.data;
  })
  .catch(error => {
    console.error(error);
  })

  loading.value.kpis = false;

}

const fetchData = async () => {
  fetchKpis();
  fetchLeadsByRange();
}

fetchData();
</script>

<style></style>
